import React from 'react';
import {
    Show,
    TextField,
    DateField,
    TopToolbar,
    ListButton,
    useRecordContext,
} from 'react-admin';
import { Box, Chip } from '@mui/material';
import { appointmentTypesFields } from '../appointment-types.model';
import { DATE_TIME_OPTIONS } from '../../app/app.component';
import { SectionedShowLayout, LabeledField } from '../../components/layouts';
import DefaultPlaceholderField from '../../material/defPlaceholderField.component';
import YesNoField from '../../material/yesNoField.component';

/**
 * Show actions (toolbar)
 */
const ShowActions = () => (
    <TopToolbar>
        <ListButton />
    </TopToolbar>
);

/**
 * Contact Types Display Component
 */
const ContactTypesField = () => {
    const record = useRecordContext();
    
    if (!record) return null;

    const contactTypes = record[appointmentTypesFields.availableContactTypes];
    
    if (!contactTypes || !Array.isArray(contactTypes) || contactTypes.length === 0) {
        return <span>-</span>;
    }
    
    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {contactTypes.map((type, index) => (
                <Chip 
                    key={index}
                    label={type}
                    size="small" 
                    color="primary"
                    variant="outlined"
                />
            ))}
        </Box>
    );
};

/**
 * Length Display Component
 */
const LengthField = () => {
    const record = useRecordContext();
    
    if (!record) return null;
    
    const minutes = record[appointmentTypesFields.length];
    
    if (!minutes && minutes !== 0) {
        return <span>-</span>;
    }
    
    return <span>{minutes} minutes</span>;
};

/**
 * Pricing Display Component
 */
const PricingField = () => {
    const record = useRecordContext();
    
    if (!record) return null;

    const pricingInfo = record[appointmentTypesFields.pricingInfo];
    
    if (!pricingInfo || !pricingInfo.price) {
        return <span>-</span>;
    }

    return <span>${pricingInfo.price}</span>;
};

/**
 * Appointment Types Show Component
 * 
 * Displays detailed information about a single appointment type using SectionedShowLayout.
 * Read-only for Phase 1 (no edit/delete yet).
 */
const AppointmentTypesShow = () => {
    const sections = [
        {
            title: "Basic Information",
            fields: [
                <LabeledField key="id" label="Healthie ID">
                    <DefaultPlaceholderField source={appointmentTypesFields.id} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="name" label="Name">
                    <DefaultPlaceholderField source={appointmentTypesFields.name} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="clientDisplayName" label="Client Display Name">
                    <DefaultPlaceholderField source={appointmentTypesFields.clientDisplayName} wrapField={<TextField/>} />
                </LabeledField>,
                <LabeledField key="length" label="Length">
                    <LengthField />
                </LabeledField>,
                <LabeledField key="pricing" label="Pricing">
                    <PricingField />
                </LabeledField>,
                <LabeledField key="position" label="Position">
                    <DefaultPlaceholderField source={appointmentTypesFields.position} wrapField={<TextField/>} />
                </LabeledField>
            ]
        },
        {
            title: "Contact Types",
            fields: [
                <LabeledField key="contactTypes" label="Available Contact Types">
                    <ContactTypesField />
                </LabeledField>
            ]
        },
        {
            title: "Booking Settings",
            fields: [
                <LabeledField key="clientsCanBook" label="Clients Can Book">
                    <YesNoField source={appointmentTypesFields.clientsCanBook} />
                </LabeledField>,
                <LabeledField key="isGroup" label="Group Appointment">
                    <YesNoField source={appointmentTypesFields.isGroup} />
                </LabeledField>,
                <LabeledField key="bookableByGroups" label="Bookable by Groups">
                    <YesNoField source={appointmentTypesFields.bookableByGroups} />
                </LabeledField>,
                <LabeledField key="bookableWithoutGroup" label="Bookable Without Group">
                    <YesNoField source={appointmentTypesFields.bookableWithoutGroup} />
                </LabeledField>,
                <LabeledField key="isWaitlistEnabled" label="Waitlist Enabled">
                    <YesNoField source={appointmentTypesFields.isWaitlistEnabled} />
                </LabeledField>,
                <LabeledField key="requireCcAtBooking" label="Require Credit Card">
                    <YesNoField source={appointmentTypesFields.requireCcAtBooking} />
                </LabeledField>
            ]
        },
        {
            title: "Provider & Location Requirements",
            fields: [
                <LabeledField key="requireSpecificProviders" label="Require Specific Providers">
                    <YesNoField source={appointmentTypesFields.requireSpecificProviders} />
                </LabeledField>,
                <LabeledField key="requireInStateClients" label="Require In-State Clients">
                    <YesNoField source={appointmentTypesFields.requireInStateClients} />
                </LabeledField>,
                <LabeledField key="insuranceBillingEnabled" label="Insurance Billing Enabled">
                    <YesNoField source={appointmentTypesFields.insuranceBillingEnabled} />
                </LabeledField>
            ]
        },
        {
            title: "Timestamps",
            fields: [
                <LabeledField key="createdAt" label="Created At">
                    <DefaultPlaceholderField source={appointmentTypesFields.createdAt} options={DATE_TIME_OPTIONS} wrapField={<DateField/>} />
                </LabeledField>,
                <LabeledField key="updatedAt" label="Updated At">
                    <DefaultPlaceholderField source={appointmentTypesFields.updatedAt} options={DATE_TIME_OPTIONS} wrapField={<DateField/>} />
                </LabeledField>,
                <LabeledField key="deletedAt" label="Deleted At">
                    <DefaultPlaceholderField source={appointmentTypesFields.deletedAt} options={DATE_TIME_OPTIONS} wrapField={<DateField/>} />
                </LabeledField>
            ]
        }
    ];

    return (
        <Show actions={<ShowActions />}>
            <SectionedShowLayout sections={sections} />
        </Show>
    );
};

export default AppointmentTypesShow;

